import React, { useState, useEffect, ReactNode } from 'react'
import { DarkModeContext, type ColorScheme } from './DarkModeContext'

interface DarkModeProviderProps {
    children: ReactNode
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
    const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
        // Check localStorage first, then system preference
        const stored = localStorage.getItem('color-scheme') as ColorScheme
        if (stored && (stored === 'light' || stored === 'dark')) {
            return stored
        }

        // Check system preference
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark'
        }

        return 'light'
    })

    const toggleColorScheme = () => {
        setColorScheme(prev => prev === 'light' ? 'dark' : 'light')
    }

    useEffect(() => {
        // Save to localStorage
        localStorage.setItem('color-scheme', colorScheme)

        // Apply to document
        document.documentElement.setAttribute('data-mantine-color-scheme', colorScheme)

        // Update theme-color meta tag
        const themeColorMeta = document.querySelector('meta[name="theme-color"]')
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', colorScheme === 'dark' ? '#1a1b1e' : '#5a965e')
        }
    }, [colorScheme])

    const value = {
        colorScheme,
        toggleColorScheme,
        setColorScheme,
    }

    return (
        <DarkModeContext.Provider value={value}>
            {children}
        </DarkModeContext.Provider>
    )
} 